import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Filler,
    ArcElement,
    Tooltip,
    Legend,
} from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import { fmt } from "../finance/finance";
import "./home.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Filler, ArcElement, Tooltip, Legend);

const todayISO = () => new Date().toISOString().slice(0, 10);
// первое число текущего календарного месяца
const thisMonthStartISO = () => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().slice(0, 10);
};
// скользящее окно "N месяцев назад от сегодня" (не календарный месяц)
const monthsBackISO = (n) => {
    const d = new Date();
    d.setMonth(d.getMonth() - n);
    return d.toISOString().slice(0, 10);
};

const COLORS = {
    teal: "#146B67",
    amber: "#D68A34",
    red: "#C1503F",
    violet: "#5B5B8C",
    border: "#E1E5E8",
    inkSoft: "#5C6B7A",
};

const PERIODS = [
    { key: "month", label: "Этот месяц", from: thisMonthStartISO },
    { key: "1m", label: "1 мес.", from: () => monthsBackISO(1) },
    { key: "3m", label: "3 мес.", from: () => monthsBackISO(3) },
    { key: "6m", label: "6 мес.", from: () => monthsBackISO(6) },
    { key: "12m", label: "12 мес.", from: () => monthsBackISO(12) },
];

const baseOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false, axis: "x" },
    plugins: { legend: { display: false }, tooltip: { mode: "index", intersect: false, axis: "x" } },
};

// Горизонтальные bar-чарты (indexAxis: "y") должны искать ближайшую точку по оси Y,
// а не X — иначе наведение всегда резолвится в первую категорию (баг Chart.js по умолчанию).
const hBarOptions = {
    ...baseOptions,
    indexAxis: "y",
    interaction: { mode: "index", intersect: false, axis: "y" },
    plugins: { ...baseOptions.plugins, tooltip: { mode: "index", intersect: false, axis: "y" } },
};

const Home = ({ logout, user, API_URL }) => {
    const [period, setPeriod] = useState("month");
    const [dateFrom, setDateFrom] = useState(thisMonthStartISO());
    const [dateTo, setDateTo] = useState(todayISO());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const applyPeriod = (key) => {
        setPeriod(key);
        const p = PERIODS.find((x) => x.key === key);
        if (p) { setDateFrom(p.from()); setDateTo(todayISO()); }
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: res } = await axios.post(`${API_URL}/getAnalyticsDashboard`, { dateFrom, dateTo });
            if (res.status === "success") setData(res);
            else toast.error("Не удалось загрузить аналитику");
        } catch (err) {
            console.error("getAnalyticsDashboard:", err);
            toast.error("Ошибка соединения с сервером");
        } finally {
            setLoading(false);
        }
    }, [API_URL, dateFrom, dateTo]);

    useEffect(() => { load(); }, [load]);

    const labels = useMemo(() => (data ? (data.months || []).map((m) => m.label) : []), [data]);

    if (loading || !data) {
        return (
            <div className="an-page">
                <div className="an-empty">Загрузка аналитики…</div>
            </div>
        );
    }

    const { kpi } = data;

    return (
        <div className="an-page">
            <div className="an-head">
                <div>
                    <h1 className="an-title">Аналитика</h1>
                    <div className="an-sub">Обзор по всем направлениям — заказы, расходы, занятость, долги</div>
                </div>
                <div className="an-period">
                    {PERIODS.map((p) => (
                        <button key={p.key} className={period === p.key ? "active" : ""} onClick={() => applyPeriod(p.key)}>
                            {p.label}
                        </button>
                    ))}
                    <button className={period === "custom" ? "active" : ""} onClick={() => setPeriod("custom")}>
                        Период…
                    </button>
                </div>
            </div>

            {period === "custom" && (
                <div className="an-custom-range">
                    <div className="an-field">
                        <label>От</label>
                        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                    </div>
                    <div className="an-field">
                        <label>До</label>
                        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                    </div>
                </div>
            )}

            <div className="an-kpi-row">
                <div className="an-kpi an-accent-teal">
                    <div className="an-kpi-lbl">Выручка</div>
                    <div className="an-kpi-val">${fmt(kpi.revenueUsd)}</div>
                </div>
                <div className="an-kpi an-accent-amber">
                    <div className="an-kpi-lbl">Расходы</div>
                    <div className="an-kpi-val">
                        {kpi.expensesUzs ? `${fmt(kpi.expensesUzs)} UZS` : ""}
                        {kpi.expensesUzs && kpi.expensesUsd ? " · " : ""}
                        {kpi.expensesUsd ? `$${fmt(kpi.expensesUsd)}` : ""}
                    </div>
                </div>
                <div className="an-kpi">
                    <div className="an-kpi-lbl">Заказов</div>
                    <div className="an-kpi-val">{kpi.ordersCount}</div>
                </div>
                <div className="an-kpi an-accent-red">
                    <div className="an-kpi-lbl">Просроченный долг</div>
                    <div className="an-kpi-val">${fmt(kpi.overdueDebtUsd)}</div>
                </div>
            </div>

            <div className="an-currency-row">
                {(data.currencySummary || []).map((c) => (
                    <div className="an-currency-card" key={c.currency}>
                        <div className="an-currency-code">{c.currency}</div>
                        <div className="an-currency-line">
                            <span className="an-currency-lbl">Приход</span>
                            <span className="an-currency-val an-income">{fmt(c.income)}</span>
                        </div>
                        <div className="an-currency-line">
                            <span className="an-currency-lbl">Расход</span>
                            <span className="an-currency-val an-expense">{fmt(c.expense)}</span>
                        </div>
                        <div className="an-currency-line an-currency-line--total">
                            <span className="an-currency-lbl">{c.profit >= 0 ? "Прибыль" : "Убыток"}</span>
                            <span className={`an-currency-val ${c.profit >= 0 ? "an-income" : "an-expense"}`}>
                                {fmt(Math.abs(c.profit))}
                            </span>
                        </div>
                    </div>
                ))}
            </div>

            {/* ── Деньги ── */}
            <div className="an-section-lbl">Деньги</div>
            <div className="an-grid-2">
                <div className="an-panel">
                    <div className="an-panel-head">
                        <div className="an-panel-title">Выручка и расходы по месяцам</div>
                        <div className="an-panel-note">По датам фактических платежей</div>
                    </div>
                    <div className="an-chart-wrap">
                        <Line
                            data={{
                                labels,
                                datasets: [
                                    {
                                        label: "Выручка, $", yAxisID: "y",
                                        data: (data.revenueByMonth || []).map((r) => r.usd),
                                        borderColor: COLORS.teal, backgroundColor: "rgba(20,107,103,0.08)",
                                        fill: true, tension: 0.35, pointRadius: 0, borderWidth: 2.5,
                                    },
                                    {
                                        label: "Расходы, UZS", yAxisID: "y1",
                                        data: (data.expensesByMonth || []).map((r) => r.uzs),
                                        borderColor: COLORS.amber, backgroundColor: "rgba(214,138,52,0.06)",
                                        fill: true, tension: 0.35, pointRadius: 0, borderWidth: 2.5,
                                    },
                                ],
                            }}
                            options={{
                                ...baseOptions,
                                plugins: { ...baseOptions.plugins, legend: { display: true, position: "bottom" } },
                                scales: {
                                    x: { grid: { display: false } },
                                    y: { position: "left", grid: { color: "#EEF1F2" }, ticks: { callback: (v) => "$" + v / 1000 + "k" } },
                                    y1: { position: "right", grid: { display: false }, ticks: { callback: (v) => (v / 1e6).toFixed(0) + "М" } },
                                },
                            }}
                        />
                    </div>
                </div>

                <div className="an-panel">
                    <div className="an-panel-head">
                        <div className="an-panel-title">Прочий приход</div>
                        <div className="an-panel-note">по источникам</div>
                    </div>
                    {data.miscIncomeBySource.length ? (
                        <div className="an-donut-wrap">
                            <Doughnut
                                data={{
                                    labels: (data.miscIncomeBySource || []).map((r) => `${r.source} (${r.currency})`),
                                    datasets: [{
                                        data: (data.miscIncomeBySource || []).map((r) => r.total),
                                        backgroundColor: [COLORS.teal, COLORS.amber, COLORS.violet, COLORS.red, "#8FAFAD", "#C9A56A"],
                                        borderWidth: 0,
                                    }],
                                }}
                                options={{ ...baseOptions, cutout: "70%", plugins: { legend: { display: true, position: "bottom", labels: { boxWidth: 9, font: { size: 11 } } } } }}
                            />
                        </div>
                    ) : <div className="an-empty-inline">Нет данных за период</div>}
                </div>
            </div>

            {/* ── Расходы ── */}
            <div className="an-section-lbl">Расходы</div>
            <div className="an-grid-2-even">
                <div className="an-panel">
                    <div className="an-panel-head">
                        <div className="an-panel-title">Расходы по категориям</div>
                        <div className="an-panel-note">за период</div>
                    </div>
                    <div className="an-chart-wrap an-chart-tall">
                        <Bar
                            data={{
                                labels: (data.expensesByCategory || []).map((c) => c.name),
                                datasets: [{
                                    data: (data.expensesByCategory || []).map((c) => c.UZS),
                                    backgroundColor: COLORS.teal, borderRadius: 4, barThickness: 16,
                                }],
                            }}
                            options={{
                                ...hBarOptions,
                                scales: {
                                    x: { grid: { color: "#EEF1F2" }, ticks: { callback: (v) => (v / 1e6).toFixed(0) + "М" } },
                                    y: { grid: { display: false } },
                                },
                            }}
                        />
                    </div>
                </div>

                <div className="an-panel">
                    <div className="an-panel-head">
                        <div className="an-panel-title">Заказы по месяцам — по типу</div>
                        <div className="an-panel-note">Индивидуальный / Share Tour / Партнёр</div>
                    </div>
                    <div className="an-chart-wrap an-chart-tall">
                        <Bar
                            data={{
                                labels,
                                datasets: [
                                    { label: "Индивидуальный", data: (data.ordersByMonth || []).map((r) => r.individual), backgroundColor: COLORS.teal, borderRadius: 3 },
                                    { label: "Share Tour", data: (data.ordersByMonth || []).map((r) => r.sharetour), backgroundColor: COLORS.amber, borderRadius: 3 },
                                    { label: "Партнёр", data: (data.ordersByMonth || []).map((r) => r.partners), backgroundColor: COLORS.violet, borderRadius: 3 },
                                ],
                            }}
                            options={{
                                ...baseOptions,
                                plugins: { ...baseOptions.plugins, legend: { display: true, position: "bottom" } },
                                scales: { x: { stacked: true, grid: { display: false } }, y: { stacked: true, grid: { color: "#EEF1F2" } } },
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* ── Транспорт, водители и кафе ── */}
            <div className="an-section-lbl">Транспорт, водители и кафе</div>
            <div className="an-grid-3">
                <div className="an-panel">
                    <div className="an-panel-head">
                        <div className="an-panel-title">Расходы по машинам</div>
                        <div className="an-panel-note">заправка + ремонт, UZS (ремонт в $ — отдельной колонкой)</div>
                    </div>
                    <div className="an-chart-wrap an-chart-tall">
                        <Bar
                            data={{
                                labels: (data.vehicleCosts || []).map((v) => v.name),
                                datasets: [
                                    { label: "Заправка", data: (data.vehicleCosts || []).map((v) => v.fuel), backgroundColor: COLORS.red, borderRadius: 3 },
                                    { label: "Ремонт", data: (data.vehicleCosts || []).map((v) => v.repairUzs), backgroundColor: COLORS.violet, borderRadius: 3 },
                                ],
                            }}
                            options={{
                                ...hBarOptions,
                                plugins: { ...hBarOptions.plugins, legend: { display: true, position: "bottom" } },
                                scales: { x: { stacked: true, grid: { color: "#EEF1F2" }, ticks: { callback: (v) => (v / 1e6).toFixed(0) + "М" } }, y: { stacked: true, grid: { display: false } } },
                            }}
                        />
                    </div>
                    <div className="an-table-wrap" style={{ marginTop: 18 }}>
                        <table>
                            <thead>
                                <tr><th>Машина</th><th style={{ textAlign: "right" }}>Заправка, UZS</th><th style={{ textAlign: "right" }}>Ремонт, UZS</th><th style={{ textAlign: "right" }}>Ремонт, $</th></tr>
                            </thead>
                            <tbody>
                                {(data.vehicleCosts || []).map((v) => (
                                    <tr key={v.name}>
                                        <td className="an-route">{v.name}</td>
                                        <td className="an-num">{fmt(v.fuel)}</td>
                                        <td className="an-num">{fmt(v.repairUzs)}</td>
                                        <td className="an-num">{v.repairUsd ? `$${fmt(v.repairUsd)}` : "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="an-panel">
                    <div className="an-panel-head">
                        <div className="an-panel-title">По водителям</div>
                        <div className="an-panel-note">выплачено за период, топ по сумме</div>
                    </div>
                    <div className="an-chart-wrap">
                        <Bar
                            data={{
                                labels: (data.driverPayouts || []).map((d) => d.name),
                                datasets: [{ data: (data.driverPayouts || []).map((d) => d.payout), backgroundColor: COLORS.teal, borderRadius: 3, barThickness: 16 }],
                            }}
                            options={{ ...hBarOptions, scales: { x: { grid: { color: "#EEF1F2" }, ticks: { callback: (v) => (v / 1e6).toFixed(0) + "М" } }, y: { grid: { display: false } } } }}
                        />
                    </div>
                    <div className="an-table-wrap" style={{ marginTop: 18 }}>
                        <table>
                            <thead>
                                <tr><th>Водитель</th><th>Машина</th><th>Выездов</th><th style={{ textAlign: "right" }}>Выплачено, UZS</th></tr>
                            </thead>
                            <tbody>
                                {(data.driverPayouts || []).map((d) => (
                                    <tr key={d.name}>
                                        <td className="an-route">{d.name}</td>
                                        <td>{d.vehicle}</td>
                                        <td>{d.trips}</td>
                                        <td className="an-num">{fmt(d.payout)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="an-panel">
                    <div className="an-panel-head">
                        <div className="an-panel-title">По кафе</div>
                        <div className="an-panel-note">выплачено за период, топ по сумме</div>
                    </div>
                    <div className="an-chart-wrap">
                        <Bar
                            data={{
                                labels: (data.cafePayouts || []).map((c) => c.name),
                                datasets: [{ data: (data.cafePayouts || []).map((c) => c.payout), backgroundColor: COLORS.amber, borderRadius: 3, barThickness: 16 }],
                            }}
                            options={{ ...hBarOptions, scales: { x: { grid: { color: "#EEF1F2" }, ticks: { callback: (v) => (v / 1e6).toFixed(0) + "М" } }, y: { grid: { display: false } } } }}
                        />
                    </div>
                    <div className="an-table-wrap" style={{ marginTop: 18 }}>
                        <table>
                            <thead>
                                <tr><th>Кафе</th><th>Заездов</th><th style={{ textAlign: "right" }}>Выплачено, UZS</th></tr>
                            </thead>
                            <tbody>
                                {(data.cafePayouts || []).map((c) => (
                                    <tr key={c.name}>
                                        <td className="an-route">{c.name}</td>
                                        <td>{c.trips}</td>
                                        <td className="an-num">{fmt(c.payout)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* ── Туры ── */}
            <div className="an-section-lbl">Туры</div>
            <div className="an-panel">
                <div className="an-panel-head">
                    <div className="an-panel-title">Топ туров за период — по выручке</div>
                    <div className="an-panel-note">включая юрты/гостевой дом/ручные брони</div>
                </div>
                <div className="an-chart-wrap an-chart-tall">
                    <Bar
                        data={{
                            labels: (data.topTours || []).map((t) => t.route),
                            datasets: [{ data: (data.topTours || []).map((t) => t.revenue), backgroundColor: COLORS.teal, borderRadius: 3, barThickness: 18 }],
                        }}
                        options={{ ...hBarOptions, scales: { x: { grid: { color: "#EEF1F2" }, ticks: { callback: (v) => "$" + (v / 1000).toFixed(0) + "k" } }, y: { grid: { display: false } } } }}
                    />
                </div>
            </div>

            <div className="an-panel" style={{ marginTop: 16 }}>
                <div className="an-panel-head">
                    <div className="an-panel-title">Детализация по турам</div>
                </div>
                <div className="an-table-wrap">
                    <table>
                        <thead>
                            <tr><th>Тур</th><th>Заказов</th><th style={{ textAlign: "right" }}>Выручка, $</th><th>Долг, $</th></tr>
                        </thead>
                        <tbody>
                            {(data.topTours || []).map((t) => (
                                <tr key={t.route}>
                                    <td className="an-route">{t.route}</td>
                                    <td>{t.count}</td>
                                    <td className="an-num">${fmt(t.revenue)}</td>
                                    <td>{t.debt > 0 ? <span className="an-pill bad">${fmt(t.debt)}</span> : <span className="an-pill ok">Нет</span>}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default Home;